import { useMemo, useState } from "react";
import {
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { trpc } from "@/lib/trpc";

type Props = {
  selectedDate: Date;
  onSelect: (d: Date) => void;
  /** compact: sidebar (cells 28px). full: popover (cells 36px). */
  size?: "compact" | "full";
};

// Abreviação de 3 letras — única opção sem ambiguidade em PT-BR
// (domingo/sábado compartilham "S", quarta/quinta compartilham "Q").
const WEEK_DAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export function AgendaMiniCalendar({ selectedDate, onSelect, size = "full" }: Props) {
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => startOfMonth(selectedDate));
  const today = new Date();

  const daysToShow = useMemo(() => {
    const first = startOfMonth(visibleMonth);
    const last = endOfMonth(visibleMonth);
    const calStart = startOfWeek(first, { weekStartsOn: 0 });
    const calEnd = endOfWeek(last, { weekStartsOn: 0 });
    const days: Date[] = [];
    let cur = calStart;
    while (cur <= calEnd) {
      days.push(new Date(cur));
      cur = new Date(cur.getTime() + 86400000);
    }
    return days;
  }, [visibleMonth]);

  // Busca consultas do mês inteiro — pro dot indicator
  const apptsQuery = trpc.appointment.listByRange.useQuery({
    rangeStart: startOfMonth(visibleMonth),
    rangeEnd: endOfMonth(visibleMonth),
  });

  const appointmentDays = useMemo(() => {
    const set = new Set<string>();
    for (const a of (apptsQuery.data ?? []) as any[]) {
      set.add(format(new Date(a.date), "yyyy-MM-dd"));
    }
    return set;
  }, [apptsQuery.data]);

  const isCompact = size === "compact";
  const cellSize = isCompact ? "h-7 w-7 text-[11px]" : "h-9 w-9 text-[13px]";
  // Texto do mês maior + mais peso — melhora leitura mesmo em sidebar estreita.
  const headerSize = isCompact ? "text-[14px]" : "text-base";
  // Weekday headers com abreviação 3 letras. Compact usa tamanho menor
  // + letter-spacing negativo pra caber em células de 28px.
  const weekdayClass = isCompact
    ? "text-[9px] tracking-tight"
    : "text-[10px] tracking-wide";

  return (
    <div className="select-none">
      {/* Header: mês/ano navegável */}
      <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setVisibleMonth((m) => subMonths(m, 1))}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-brand-50 hover:text-brand-700"
          aria-label="Mês anterior"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => setVisibleMonth(startOfMonth(today))}
          className={`rounded-md px-2 py-0.5 capitalize font-semibold text-zinc-900 transition-colors hover:bg-zinc-100 ${headerSize}`}
          title="Voltar pro mês atual"
        >
          {format(visibleMonth, "MMMM yyyy", { locale: ptBR })}
        </button>
        <button
          type="button"
          onClick={() => setVisibleMonth((m) => addMonths(m, 1))}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-brand-50 hover:text-brand-700"
          aria-label="Próximo mês"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Week day headers */}
      <div className="mb-1 grid grid-cols-7 gap-0.5">
        {WEEK_DAYS.map((wd, i) => (
          <div
            key={i}
            className={`text-center font-semibold uppercase text-zinc-400 dark:text-muted-foreground ${weekdayClass}`}
          >
            {wd}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-0.5">
        {daysToShow.map((d) => {
          const isSelected = isSameDay(d, selectedDate);
          const isToday = isSameDay(d, today);
          const inMonth = isSameMonth(d, visibleMonth);
          const key = format(d, "yyyy-MM-dd");
          const hasAppt = appointmentDays.has(key);

          const baseStyle =
            "relative mx-auto flex items-center justify-center rounded-full font-medium transition-all duration-150";
          const stateStyle = isSelected
            ? "bg-brand-600 text-white shadow-sm scale-105"
            : isToday
              ? "bg-brand-50 text-brand-700 ring-1 ring-brand-300 hover:bg-brand-100"
              : "text-zinc-700 hover:bg-zinc-100 hover:scale-105";
          const dimmed = !inMonth && !isSelected && !isToday ? "opacity-30" : "";

          return (
            <button
              key={key}
              type="button"
              onClick={() => {
                onSelect(d);
                if (!inMonth) setVisibleMonth(startOfMonth(d));
              }}
              className={`${baseStyle} ${cellSize} ${stateStyle} ${dimmed}`}
            >
              {format(d, "d")}
              {hasAppt && !isSelected && (
                <span
                  className="absolute bottom-1 h-1 w-1 rounded-full bg-brand-500"
                  aria-hidden
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
